import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save } from "lucide-react";
import { toast } from "sonner";

export default function HomepageEditor() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(r => setS(r.data));
  }, []);

  const save = async () => {
    if (!s) return;
    setSaving(true);
    try {
      const payload = {
        hero_headline_en: s.hero_headline_en || "",
        hero_headline_ar: s.hero_headline_ar || "",
        hero_sub_en: s.hero_sub_en || "",
        hero_sub_ar: s.hero_sub_ar || "",
        hero_cta1_en: s.hero_cta1_en || "",
        hero_cta1_ar: s.hero_cta1_ar || "",
        hero_cta2_en: s.hero_cta2_en || "",
        hero_cta2_ar: s.hero_cta2_ar || "",
        show_dubai_hub: !!s.show_dubai_hub,
        show_network: !!s.show_network,
        show_categories: !!s.show_categories,
        show_b2b: !!s.show_b2b,
        show_featured: !!s.show_featured,
        show_why: !!s.show_why,
      };
      const { data } = await api.put("/admin/settings", payload);
      setS(data);
      toast.success("Homepage saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  if (!s) return <p className="text-slate-500">Loading…</p>;
  const patch = (k, v) => setS(prev => ({ ...prev, [k]: v }));

  const sections = [
    ["show_dubai_hub", "Dubai Hub section"],
    ["show_network", "Global Network section"],
    ["show_categories", "Product Categories section"],
    ["show_b2b", "B2B block"],
    ["show_featured", "Featured Products section"],
    ["show_why", "Why AFC section"],
  ];

  return (
    <div className="space-y-6 max-w-4xl" data-testid="homepage-editor">
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h3 className="font-display text-base font-semibold text-[#0B132B] mb-4">Hero Section</h3>
        <p className="text-xs text-slate-500 mb-4">Leave empty to use the default translations. Any text you enter here overrides the site copy.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>Headline (English)</Label><Input value={s.hero_headline_en || ""} onChange={e => patch("hero_headline_en", e.target.value)} className="mt-1" data-testid="hero-headline-en" /></div>
          <div><Label>Headline (Arabic)</Label><Input dir="rtl" value={s.hero_headline_ar || ""} onChange={e => patch("hero_headline_ar", e.target.value)} className="mt-1" data-testid="hero-headline-ar" /></div>
          <div><Label>Sub-headline (English)</Label><Textarea rows={3} value={s.hero_sub_en || ""} onChange={e => patch("hero_sub_en", e.target.value)} className="mt-1" /></div>
          <div><Label>Sub-headline (Arabic)</Label><Textarea dir="rtl" rows={3} value={s.hero_sub_ar || ""} onChange={e => patch("hero_sub_ar", e.target.value)} className="mt-1" /></div>
          <div><Label>Primary button (English)</Label><Input value={s.hero_cta1_en || ""} onChange={e => patch("hero_cta1_en", e.target.value)} className="mt-1" /></div>
          <div><Label>Primary button (Arabic)</Label><Input dir="rtl" value={s.hero_cta1_ar || ""} onChange={e => patch("hero_cta1_ar", e.target.value)} className="mt-1" /></div>
          <div><Label>Secondary button (English)</Label><Input value={s.hero_cta2_en || ""} onChange={e => patch("hero_cta2_en", e.target.value)} className="mt-1" /></div>
          <div><Label>Secondary button (Arabic)</Label><Input dir="rtl" value={s.hero_cta2_ar || ""} onChange={e => patch("hero_cta2_ar", e.target.value)} className="mt-1" /></div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h3 className="font-display text-base font-semibold text-[#0B132B] mb-4">Section Visibility</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {sections.map(([k, label]) => (
            <label key={k} className="flex items-center justify-between border border-slate-200 rounded-xl px-4 py-3">
              <span className="text-sm">{label}</span>
              <Switch checked={s[k] !== false} onCheckedChange={v => patch(k, v)} data-testid={`toggle-${k}`} />
            </label>
          ))}
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="rounded-full bg-[#1E5FD9] hover:bg-[#174AB0]" data-testid="hp-save">
        <Save className="w-4 h-4 me-2" /> {saving ? "Saving…" : "Save Homepage"}
      </Button>
    </div>
  );
}
